/**
 * 宣传页面 (Landing Page)
 *
 * AI 智能客服系统的静态宣传页面
 * 深色科技风 + 微动画 + Glassmorphism
 */

import React from 'react';
import {
    RobotOutlined,
    ThunderboltOutlined,
    SearchOutlined,
    NodeIndexOutlined,
    ApiOutlined,
    DatabaseOutlined,
    CloudServerOutlined,
    CodeOutlined,
    RadarChartOutlined,
} from '@ant-design/icons';
import { LandingWidgetBridge } from '../components/widget/LandingWidgetBridge';
import './LandingPage.css';

/* ===== 特性数据 ===== */
const features = [
    {
        icon: <ThunderboltOutlined />,
        colorClass: 'blue',
        title: '意图识别 + RAG',
        desc: '基于大语言模型的精准意图识别，结合 RAG 检索增强生成技术，从知识库中快速定位最佳答案，实现高质量智能应答。',
        tags: ['LangGraph', 'PydanticAI', 'DeepSeek'],
    },
    {
        icon: <SearchOutlined />,
        colorClass: 'purple',
        title: '混合检索引擎',
        desc: '向量语义检索与关键词精确匹配双引擎融合，通过 RRF 算法智能排序，检索准确率 85%+。',
        tags: ['Qdrant', 'Meilisearch', 'RRF 融合'],
    },
    {
        icon: <NodeIndexOutlined />,
        colorClass: 'green',
        title: '三层对话记忆',
        desc: '工作记忆（最近 5 轮）→ 短期记忆（缓存）→ 长期记忆（数据库），从单轮交互到长期用户画像，打造连贯流畅的对话体验。',
        tags: ['工作记忆', '短期记忆', '长期画像'],
    },
    {
        icon: <ApiOutlined />,
        colorClass: 'orange',
        title: '插件化业务编排',
        desc: '基于抽象插件基类的灵活扩展架构，内置知识检索、工单创建、订单查询等插件，支持按需定制业务能力。',
        tags: ['ToolPlugin', '热插拔', '业务解耦'],
    },
];

/* ===== 适用场景数据 ===== */
const scenes = [
    {
        emoji: '🛒',
        title: '电商零售',
        desc: '商品咨询、订单查询、售后服务、退换货指引',
    },
    {
        emoji: '🏦',
        title: '金融服务',
        desc: '产品咨询、业务办理、风控提示、技术支持',
    },
    {
        emoji: '🏢',
        title: '政企服务',
        desc: '政策咨询、办事指南、投诉建议、信息查询',
    },
];

/* ===== 技术栈数据 ===== */
const techStack = [
    {
        icon: <RobotOutlined />,
        name: 'LangGraph',
        role: '对话工作流编排',
        bg: 'rgba(59, 130, 246, 0.12)',
        color: '#60a5fa',
    },
    {
        icon: <RadarChartOutlined />,
        name: 'Qdrant',
        role: '向量语义检索',
        bg: 'rgba(139, 92, 246, 0.12)',
        color: '#a78bfa',
    },
    {
        icon: <SearchOutlined />,
        name: 'Meilisearch',
        role: '关键词精确匹配',
        bg: 'rgba(249, 115, 22, 0.12)',
        color: '#fb923c',
    },
    {
        icon: <DatabaseOutlined />,
        name: 'PostgreSQL',
        role: '数据持久化 + pgvector',
        bg: 'rgba(16, 185, 129, 0.12)',
        color: '#34d399',
    },
    {
        icon: <CloudServerOutlined />,
        name: 'Redis',
        role: '会话管理 + 缓存',
        bg: 'rgba(239, 68, 68, 0.12)',
        color: '#f87171',
    },
    {
        icon: <CodeOutlined />,
        name: 'FastAPI + React',
        role: '全栈技术框架',
        bg: 'rgba(59, 130, 246, 0.12)',
        color: '#60a5fa',
    },
];

/* ===== 统计数据 ===== */
const stats = [
    { value: '75%+', label: '检索准确率' },
    { value: '<300ms', label: '响应速度' },
    { value: '60%', label: '成本降低' },
    { value: '<200MB', label: '内存占用' },
];

const LandingPage: React.FC = () => {
    return (
        <div className="landing-page">
            <LandingWidgetBridge />

            {/* ===== Hero 区域 ===== */}
            <section className="landing-hero">
                {/* 装饰轨道 */}
                <div className="landing-orbit">
                    <div className="landing-orbit-dot" />
                </div>
                <div className="landing-orbit">
                    <div className="landing-orbit-dot" />
                </div>

                <div className="landing-hero-content">
                    {/* 品牌标签 */}
                    <div className="landing-badge">
                        <span className="landing-badge-dot" />
                        AI 驱动的新一代客户服务平台
                    </div>

                    {/* 主标题 */}
                    <h1>
                        <span className="landing-gradient-text">智能客服</span>
                        <br />
                        重新定义客户服务体验
                    </h1>

                    {/* 描述 */}
                    <p className="landing-hero-desc">
                        基于混合智能路由架构、三层记忆管理与 RAG 混合检索，
                        为企业提供高效、精准、低成本的 AI 客服解决方案。
                        支持 Docker Compose 一键私有化部署。
                    </p>

                </div>
            </section>

            {/* ===== 统计数字 ===== */}
            <div className="landing-stats">
                {stats.map((stat) => (
                    <div key={stat.label} className="landing-stat">
                        <div className="landing-stat-value">{stat.value}</div>
                        <div className="landing-stat-label">{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* ===== 核心特性 ===== */}
            <section className="landing-section">
                <div className="landing-section-header">
                    <div className="landing-section-label">Core Features</div>
                    <h2 className="landing-section-title">核心能力</h2>
                    <p className="landing-section-desc">
                        四大核心模块协同驱动，打造企业级智能客服解决方案
                    </p>
                </div>

                <div className="landing-features-grid">
                    {features.map((f) => (
                        <div key={f.title} className="landing-feature-card">
                            <div className={`landing-feature-icon ${f.colorClass}`}>
                                {f.icon}
                            </div>
                            <div className="landing-feature-title">{f.title}</div>
                            <p className="landing-feature-desc">{f.desc}</p>
                            <div className="landing-feature-tags">
                                {f.tags.map((tag) => (
                                    <span key={tag} className="landing-feature-tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <div className="landing-divider" />

            {/* ===== 适用场景 ===== */}
            <section className="landing-section">
                <div className="landing-section-header">
                    <div className="landing-section-label">Use Cases</div>
                    <h2 className="landing-section-title">适用场景</h2>
                    <p className="landing-section-desc">
                        灵活适配多种行业场景，助力企业服务数字化转型
                    </p>
                </div>

                <div className="landing-scenes-grid">
                    {scenes.map((s) => (
                        <div key={s.title} className="landing-scene-card">
                            <span className="landing-scene-emoji">{s.emoji}</span>
                            <div className="landing-scene-title">{s.title}</div>
                            <p className="landing-scene-desc">{s.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <div className="landing-divider" />

            {/* ===== 技术架构 ===== */}
            <section className="landing-tech-section">
                <div className="landing-tech-inner">
                    <div className="landing-section-header">
                        <div className="landing-section-label">Tech Stack</div>
                        <h2 className="landing-section-title">技术架构</h2>
                        <p className="landing-section-desc">
                            精选业界成熟技术栈，兼顾性能与可维护性
                        </p>
                    </div>

                    <div className="landing-tech-grid">
                        {techStack.map((tech) => (
                            <div key={tech.name} className="landing-tech-item">
                                <div
                                    className="landing-tech-icon"
                                    style={{ background: tech.bg, color: tech.color }}
                                >
                                    {tech.icon}
                                </div>
                                <div>
                                    <div className="landing-tech-name">{tech.name}</div>
                                    <div className="landing-tech-role">{tech.role}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Footer ===== */}
            <footer className="landing-footer">
                <div className="landing-footer-brand">
                    <div className="landing-footer-logo">
                        <RobotOutlined />
                    </div>
                    <span className="landing-footer-name">AI 智能客服系统</span>
                </div>
                <p className="landing-footer-copy">
                    © {new Date().getFullYear()} Cloud-Star Corp. All rights reserved.
                </p>
            </footer>
        </div>
    );
};

export default LandingPage;
